import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Button, Form, Badge, Table, InputGroup, Card, Alert, Row, Col } from 'react-bootstrap';
import { FaExclamationTriangle, FaSearch, FaCheckSquare, FaSquare, FaBarcode, FaCheck, FaTimes } from 'react-icons/fa';
import apiService from '../../services/apiServices';

const REASONS = [
  "Damaged / Expired",
  "Defective / Factory Fault",
  "Theft / Loss",
  "Display / Demo Unit Reduction",
  "Warehouse Damage",
  "Other Write-off"
];

const MarkDamagedModal = ({ show, onClose, scope = 'item', item, variantId, variantIds, serial, batch, maxQty = 1 }) => {
  const isSerialized = item?.serialized;

  // Non-serialized state
  const [qty, setQty] = useState(1);

  // Serialized state
  const [selectedSerials, setSelectedSerials] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [scanInput, setScanInput] = useState('');

  // Form fields
  const [reason, setReason] = useState(REASONS[0]);
  const [customNotes, setCustomNotes] = useState('');
  const [loading, setLoading] = useState(false);

  // Filter available serials for selection
  const availableSerials = useMemo(() => {
    if (!isSerialized || !item?.inStock) return [];

    let list = item.inStock.filter(s => s.status === 'Available');

    if (variantId) {
      list = list.filter(s => String(s.variant_id) === String(variantId));
    }
    if (variantIds && variantIds.length > 0) {
      list = list.filter(s => variantIds.includes(String(s.variant_id)));
    }

    // Pre-filter by batch if scope is batch
    if (scope === 'batch' && batch) {
      list = list.filter(s => s.batch_number === batch);
    }

    // Pre-filter by single serial if scope is serial
    if (scope === 'serial' && serial) {
      list = list.filter(s => s.serialNumber === serial);
    }

    // Apply search term filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(s =>
        s.serialNumber?.toLowerCase().includes(term) ||
        s.batch_number?.toLowerCase().includes(term) ||
        s.supplier?.business_name?.toLowerCase().includes(term)
      );
    }

    return list;
  }, [item, isSerialized, scope, batch, serial, variantId, variantIds, searchTerm]);

  // Initial selection setup
  useEffect(() => {
    if (scope === 'serial' && serial) {
      setSelectedSerials([serial]);
    } else if (scope === 'batch' && batch && isSerialized && item?.inStock) {
      // Pre-select all available serials in this batch if batch scope
      let batchSerials = item.inStock.filter(s => s.batch_number === batch && s.status === 'Available');
      if (variantId) {
        batchSerials = batchSerials.filter(s => String(s.variant_id) === String(variantId));
      }
      if (variantIds && variantIds.length > 0) {
        batchSerials = batchSerials.filter(s => variantIds.includes(String(s.variant_id)));
      }
      setSelectedSerials(batchSerials.map(s => s.serialNumber));
    }
  }, [scope, serial, batch, isSerialized, item, variantId, variantIds]);

  // Handle single serial toggle
  const toggleSerial = (sn) => {
    setSelectedSerials(prev =>
      prev.includes(sn) ? prev.filter(s => s !== sn) : [...prev, sn]
    );
  };

  const allAvailableSerialsInScope = useMemo(() => {
    if (!isSerialized || !item?.inStock) return [];
    let list = item.inStock.filter(s => s.status === 'Available');
    if (variantId) {
      list = list.filter(s => String(s.variant_id) === String(variantId));
    }
    if (variantIds && variantIds.length > 0) {
      list = list.filter(s => variantIds.includes(String(s.variant_id)));
    }
    if (scope === 'batch' && batch) {
      list = list.filter(s => s.batch_number === batch);
    }
    return list;
  }, [item, isSerialized, scope, batch, variantId, variantIds]);

  // Select all total available in scope
  const handleSelectAllTotal = () => {
    const allSns = allAvailableSerialsInScope.map(s => s.serialNumber);
    setSelectedSerials(allSns);
  };

  // Select all visible / Deselect all
  const handleSelectAllVisible = () => {
    const visibleSns = availableSerials.map(s => s.serialNumber);
    const allSelected = visibleSns.every(sn => selectedSerials.includes(sn));

    if (allSelected) {
      setSelectedSerials(prev => prev.filter(sn => !visibleSns.includes(sn)));
    } else {
      setSelectedSerials(prev => Array.from(new Set([...prev, ...visibleSns])));
    }
  };

  const handleDeselectAll = () => {
    setSelectedSerials([]);
  };

  const [scanError, setScanError] = useState(null);

  // Barcode Quick Scan Handler
  const handleScanSubmit = async (e) => {
    e.preventDefault();
    if (!scanInput.trim()) return;
    setScanError(null);
    const scannedSn = scanInput.trim().toUpperCase();

    // 1. Look for serial in local availableSerials
    const localMatch = availableSerials.find(s => s.serialNumber?.toUpperCase() === scannedSn);
    if (localMatch) {
      if (!selectedSerials.includes(localMatch.serialNumber)) {
        setSelectedSerials(prev => [...prev, localMatch.serialNumber]);
      }
      setScanInput('');
      setScanError(null);
      return;
    }

    // 2. Query backend to verify status of serial across system DB
    try {
      const res = await apiService.get(`/stock/verify-serial/${encodeURIComponent(scannedSn)}`);
      if (res.data && res.data.found) {
        const st = res.data.status;
        if (st === 'Damaged' || st?.includes('Damaged')) {
          setScanError(`⚠️ Serial "${res.data.serialNumber}" is ALREADY marked as Damaged. Select from the available serials listed below:`);
        } else if (st === 'Sold') {
          setScanError(`⚠️ Serial "${res.data.serialNumber}" was ALREADY Sold. Select from the available serials listed below:`);
        } else {
          setScanError(`⚠️ Serial "${res.data.serialNumber}" status is "${st}" (cannot mark as damaged).`);
        }
      } else {
        setScanError(`❌ Serial "${scannedSn}" was not found in database.`);
      }
    } catch (err) {
      setScanError(`❌ Failed to verify serial "${scannedSn}".`);
    }
  };

  // Submit Handler
  const handleSubmit = async () => {
    if (!reason) return alert('Please select a reason.');

    if (isSerialized && selectedSerials.length === 0) {
      return alert('Please select at least one Serial Number / IMEI to mark as damaged.');
    }

    if (!isSerialized && (qty <= 0 || qty > maxQty)) {
      return alert(`Please specify a valid quantity between 1 and ${maxQty}.`);
    }

    const finalReason = customNotes ? `${reason}: ${customNotes}` : reason;

    setLoading(true);
    try {
      const payload = {
        itemId: item._id,
        variantId: variantId || null,
        adjustmentType: 'Deduct',
        reason: finalReason,
        adjustmentQty: isSerialized ? selectedSerials.length : qty,
        ...(isSerialized ? { targetSerialNumbers: selectedSerials } : {}),
        ...(batch ? { batchNumber: batch } : {}),
      };

      await apiService.post('/stock/adjust', payload);
      onClose(true);
    } catch (err) {
      console.error('Mark Damaged Error:', err);
      alert(err.response?.data?.message || 'Failed to mark stock as damaged.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={() => onClose(false)} centered size={isSerialized ? "lg" : "md"}>
      <Modal.Header closeButton className="bg-danger bg-opacity-10 border-danger border-opacity-25">
        <Modal.Title className="fs-6 fw-bold text-danger d-flex align-items-center gap-2">
          <FaExclamationTriangle /> Mark Stock as Damaged / Write-Off
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3">
        {/* Item Header Banner */}
        <Card className="border-0 bg-light mb-3">
          <Card.Body className="p-2 px-3 d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-bold text-dark">{item?.displayName || item?.itemName}</div>
              <small className="text-muted">SKU: {item?.barcode || 'N/A'}</small>
            </div>
            <div className="text-end">
              <Badge bg={isSerialized ? "info" : "secondary"} className="me-2">
                {isSerialized ? "Serialized" : "Non-Serialized"}
              </Badge>
              <Badge bg="success">
                Available: {isSerialized ? (item?.stockSummary?.available ?? item?.inStock?.length ?? 0) : maxQty} Units
              </Badge>
            </div>
          </Card.Body>
        </Card>

        {/* SERIALIZED SECTION */}
        {isSerialized ? (
          <div>
            {/* Quick Barcode Scanner Box */}
            <Form onSubmit={handleScanSubmit} className="mb-3">
              <Form.Label className="small fw-bold text-muted d-flex align-items-center gap-1">
                <FaBarcode /> Quick Scan / Type Serial / IMEI
              </Form.Label>
              <InputGroup size="sm" className="mb-2">
                <Form.Control
                  type="text"
                  placeholder="Scan or type serial number and press Enter..."
                  value={scanInput}
                  onChange={e => {
                    setScanInput(e.target.value);
                    if (scanError) setScanError(null);
                  }}
                  className="font-monospace fw-bold"
                />
                <Button type="submit" variant="dark">Add Serial</Button>
              </InputGroup>
              {scanError && (
                <Alert variant="danger" className="d-flex justify-content-between align-items-center py-2 px-3 small mb-0 fw-semibold">
                  <span>{scanError}</span>
                  <FaTimes
                    className="ms-2 text-danger opacity-75 fs-6"
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                    onClick={() => setScanError(null)}
                    title="Dismiss alert"
                  />
                </Alert>
              )}
            </Form>

            {/* Filter & Selection Toolbar */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <InputGroup size="sm" style={{ maxWidth: 300 }}>
                <InputGroup.Text><FaSearch /></InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Filter available serials..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </InputGroup>

              <div className="d-flex align-items-center gap-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={handleSelectAllVisible}
                  disabled={availableSerials.length === 0}
                >
                  {availableSerials.length > 0 && availableSerials.every(s => selectedSerials.includes(s.serialNumber))
                    ? "Deselect All Visible"
                    : "Select All Visible"}
                </Button>
                <Badge bg="danger" className="px-3 py-2 fs-6 fw-bold">
                  Selected: {selectedSerials.length} Serials
                </Badge>
              </div>
            </div>

            {/* Serials Selection Table */}
            <div className="border rounded bg-white overflow-auto mb-3" style={{ maxHeight: '280px' }}>
              <Table hover size="sm" className="mb-0 align-middle">
                <thead className="bg-light sticky-top shadow-sm small text-uppercase text-muted">
                  <tr>
                    <th style={{ width: 40 }} className="text-center">Select</th>
                    <th>Serial Number / IMEI</th>
                    <th>Batch #</th>
                    <th>Supplier</th>
                    <th className="text-end">Cost</th>
                    <th className="text-end">Selling</th>
                  </tr>
                </thead>
                <tbody>
                  {availableSerials.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4 text-muted">
                        No available serials match your filter criteria.
                      </td>
                    </tr>
                  ) : (
                    availableSerials.map(s => {
                      const isSelected = selectedSerials.includes(s.serialNumber);
                      return (
                        <tr
                          key={s.serialNumber}
                          onClick={() => toggleSerial(s.serialNumber)}
                          style={{ cursor: 'pointer' }}
                          className={isSelected ? "table-danger bg-opacity-10" : ""}
                        >
                          <td className="text-center">
                            {isSelected ? (
                              <FaCheckSquare className="text-danger fs-5" />
                            ) : (
                              <FaSquare className="text-muted opacity-50 fs-5" />
                            )}
                          </td>
                          <td className="fw-bold font-monospace text-dark">
                            {s.serialNumber}
                          </td>
                          <td className="small text-muted font-monospace">{s.batch_number || '-'}</td>
                          <td className="small text-muted">{s.supplier?.business_name || '-'}</td>
                          <td className="text-end small text-muted">Rs. {s.unitCost || 0}</td>
                          <td className="text-end small fw-bold text-dark">Rs. {s.sellingPrice || 0}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        ) : (
          /* NON-SERIALIZED SECTION */
          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold text-muted">
              Quantity to Mark Damaged (Available: {maxQty})
            </Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={maxQty}
              value={qty}
              onChange={e => setQty(Math.min(maxQty, Math.max(1, parseInt(e.target.value) || 1)))}
            />
          </Form.Group>
        )}

        {/* REASON & NOTES */}
        <Row className="g-2">
          <Col md={6}>
            <Form.Group className="mb-2">
              <Form.Label className="small fw-bold text-muted">Damage Reason</Form.Label>
              <Form.Select value={reason} onChange={e => setReason(e.target.value)}>
                {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-2">
              <Form.Label className="small fw-bold text-muted">Additional Audit Notes</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Cracked screen during transport"
                value={customNotes}
                onChange={e => setCustomNotes(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="p-2 bg-light d-flex justify-content-between">
        <Button variant="outline-secondary" size="sm" onClick={() => onClose(false)}>
          Cancel
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={handleSubmit}
          disabled={loading || (isSerialized && selectedSerials.length === 0)}
          className="px-4 fw-bold"
        >
          {loading ? 'Processing Write-off...' : isSerialized ? `Confirm Write-off (${selectedSerials.length} Units)` : `Confirm Write-off (${qty} Units)`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MarkDamagedModal;
